import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../../../../../lib/analytics';
import { Task } from '../../../../../types/task';
import { TaskItemShared } from '../../tasks/base';
import { FocusTaskLogic, useFocusTimer } from '../components';
import {
  playingTaskIdsAtom,
  addPlayingTaskAtom,
  removePlayingTaskAtom,
  setFocusedTaskAtom,
  focusedTaskIdAtom,
  getDisplayedTaskProgressAtom,
  updateUserSetTaskProgressAtom,
  userSetTaskProgressAtom,
  startTaskTrackingAtom,
  stopTaskTrackingAtom,
  getEphemeralSessionRemainingTimeAtom,
  ephemeralTaskSessionsAtom,
} from '../../../../../atoms/v3/focusAtoms';
import { showUndoToastAtom } from '../../../../../atoms/v3/undoToastAtoms';

interface UseFocusTaskItemProps {
  task: Task;
  onToggle: (taskId: string) => void;
  onClick: (taskId: string) => void;
  isLast: boolean;
}

/**
 * 专注任务项的业务逻辑Hook
 * 包含所有状态管理、计算逻辑和事件处理
 */
export const useFocusTaskItem = ({
  task,
  onToggle,
  onClick,
  isLast
}: UseFocusTaskItemProps) => {
  const { t } = useTranslation(['v3/focus', 'common']);
  
  // Atoms订阅
  const playingTaskIds = useAtomValue(playingTaskIdsAtom);
  const [, addPlayingTask] = useAtom(addPlayingTaskAtom);
  const [, stopTaskTracking] = useAtom(stopTaskTrackingAtom);
  const focusedTaskId = useAtomValue(focusedTaskIdAtom);
  const [, setFocusedTask] = useAtom(setFocusedTaskAtom);
  const [, showUndoToast] = useAtom(showUndoToastAtom);
  const getTaskProgress = useAtomValue(getDisplayedTaskProgressAtom);
  const [, updateUserProgress] = useAtom(updateUserSetTaskProgressAtom);
  const userProgressMap = useAtomValue(userSetTaskProgressAtom);
  const [, startSession] = useAtom(startTaskTrackingAtom);
  const getRemainingTime = useAtomValue(getEphemeralSessionRemainingTimeAtom);
  const ephemeralSessions = useAtomValue(ephemeralTaskSessionsAtom);

  // 本地状态
  const [isDraggingProgressBar, setIsDraggingProgressBar] = useState(false);
  const [localDragProgress, setLocalDragProgress] = useState<number | null>(null);
  const justFinishedDragRef = useRef(false);
  const [, forceUpdate] = useState(0);
  const [showDurationPopover, setShowDurationPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const popoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const playButtonRef = useRef<HTMLButtonElement>(null);

  // 计算状态
  const isPlaying = playingTaskIds.includes(task.id);
  const isFocused = focusedTaskId === task.id;
  const remainingTimeMs = getRemainingTime(task.id);

  // 样式计算 - 使用共享工具类
  const backgroundColorClass = useMemo(() => {
    return TaskItemShared.getBackgroundColorClass();
  }, []);

  const checkboxColorClass = useMemo(() => {
    return TaskItemShared.getCheckboxColorClass(task);
  }, [task.done, task.vital, task.urgent]);

  const borderStyleClass = useMemo(() => {
    return TaskItemShared.getBorderStyleClass(isLast);
  }, [isLast]);

  // 格式化函数 - 使用共享工具类
  const formatDate = useCallback(
    TaskItemShared.createFormatDate(t),
    [t]
  );

  const formatRecurrenceFrequency = useCallback(
    TaskItemShared.createFormatRecurrenceFrequency(t, 'v3/focus'),
    [t]
  );

  const formatRemainingTime = useCallback(
    FocusTaskLogic.formatRemainingTime,
    []
  );

  // 专注逻辑计算 - 使用专注工具类
  const isTaskProgressDraggable = useMemo(() => {
    return FocusTaskLogic.isTaskProgressDraggable(task);
  }, [task]);

  const currentProgress = useMemo(() => {
    return FocusTaskLogic.calculateCurrentProgress(
      isDraggingProgressBar,
      localDragProgress,
      isTaskProgressDraggable,
      getTaskProgress,
      task.id
    );
  }, [isDraggingProgressBar, localDragProgress, isTaskProgressDraggable, getTaskProgress, task.id]);

  const isTimerComplete = useMemo(() => {
    return FocusTaskLogic.isTimerComplete(
      isPlaying,
      isTaskProgressDraggable,
      userProgressMap,
      task.id,
      currentProgress
    );
  }, [isPlaying, isTaskProgressDraggable, userProgressMap, task.id, currentProgress]);

  // 专注模式的额外复选框逻辑
  const additionalCheckboxLogic = useCallback(() => {
    const newDoneState = !task.done;
    if (newDoneState && isPlaying) {
      stopTaskTracking(task.id);
      if (isFocused) {
        setFocusedTask(null);
      }
    }
  }, [task.done, isPlaying, isFocused, stopTaskTracking, setFocusedTask]);

  // 处理复选框点击 - 使用共享工具类，但添加专注逻辑
  const { handleCheckboxClick } = useMemo(() => {
    const handlers = TaskItemShared.createEventHandlers(
      task,
      onToggle,
      onClick,
      showUndoToast,
      (event: string, data: any) => trackEvent(event, { ...data, from_focus_task_item: true }),
      additionalCheckboxLogic
    );
    return handlers;
  }, [task, onToggle, onClick, showUndoToast, additionalCheckboxLogic]);

  // 处理任务项点击 - 专注模式需要检查拖拽状态
  const handleItemClick = useCallback(() => {
    if (justFinishedDragRef.current) {
      justFinishedDragRef.current = false;
      return;
    }
    if (isDraggingProgressBar) {
      return;
    }
    trackEvent('task detail viewed', {
      task_id: task.id,
      task_title: task.title.substring(0, 50),
      is_completed: task.done,
      from_focus_task_item: true,
    });
    onClick(task.id);
  }, [task, onClick, isDraggingProgressBar]);

  // 使用专注逻辑工具类创建播放控制事件处理器
  const {
    handlePlayClick,
    handleDurationSelect,
    handlePauseClick,
    handleRestartClick
  } = useMemo(() => {
    return FocusTaskLogic.createPlayControlHandlers(
      task,
      startSession,
      stopTaskTracking,
      setFocusedTask,
      setShowDurationPopover,
      trackEvent,
      ephemeralSessions,
      isFocused
    );
  }, [task, startSession, stopTaskTracking, setFocusedTask, setShowDurationPopover, trackEvent, ephemeralSessions, isFocused]);

  // 使用专注逻辑工具类创建弹窗控制事件处理器
  const {
    handleMouseEnterPlayButton,
    handleMouseLeavePlayButtonContainer,
    handleMouseEnterPopover,
    handleMouseLeavePopover
  } = useMemo(() => {
    return FocusTaskLogic.createPopoverHandlers(
      isPlaying,
      playButtonRef,
      setPopoverPosition,
      setShowDurationPopover,
      popoverTimeoutRef
    );
  }, [isPlaying]);

  // 使用专注定时器Hook
  useFocusTimer(
    isPlaying,
    isTaskProgressDraggable,
    userProgressMap,
    task.id,
    getRemainingTime,
    forceUpdate
  );

  // Effect to reset localDragProgress if isPlaying changes or task changes
  useEffect(() => {
    if (!isPlaying) {
      setLocalDragProgress(null);
    }
    forceUpdate(prev => prev + 1);
  }, [isPlaying, task.id]);

  return {
    // 状态
    isPlaying,
    isFocused,
    isDraggingProgressBar,
    setIsDraggingProgressBar,
    localDragProgress,
    setLocalDragProgress,
    justFinishedDragRef,
    showDurationPopover,
    popoverPosition,
    playButtonRef,
    remainingTimeMs,
    
    // 计算值
    backgroundColorClass,
    checkboxColorClass,
    borderStyleClass,
    isTaskProgressDraggable,
    currentProgress,
    isTimerComplete,
    
    // 格式化函数
    formatDate,
    formatRecurrenceFrequency,
    formatRemainingTime,
    
    // 事件处理器
    handleCheckboxClick,
    handleItemClick,
    handlePlayClick,
    handleDurationSelect,
    handlePauseClick,
    handleRestartClick,
    handleMouseEnterPlayButton,
    handleMouseLeavePlayButtonContainer,
    handleMouseEnterPopover,
    handleMouseLeavePopover,
    
    // 其他数据
    userProgressMap,
    updateUserProgress,
    trackEvent,
    t
  };
};
